/**
 * Focus the first marked field after submit settles (TMMRSL-0056, FRM-005).
 * The field is disabled while `submitting`, so a `focus()` in the catch is ignored.
 * `target` is cleared after the focus.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

function focusControl(node: unknown): void {
  // A text field's ref is the input. MUI Select's `inputRef` is an imperative handle whose
  // `focus()` moves to the combobox (the hidden native input is not focusable).
  if (node !== null && typeof node === 'object' && 'focus' in node && typeof node.focus === 'function') {
    const focusable = node as { focus: () => void };
    focusable.focus();
  }
}

export function useFocusOnError<K extends string>(
  submitting: boolean,
  target: K | null,
  refs: Partial<Record<K, RefObject<HTMLElement | null>>>,
  clear: () => void,
): void {
  const refsRef = useRef(refs);
  const clearRef = useRef(clear);
  useLayoutEffect(() => {
    refsRef.current = refs;
    clearRef.current = clear;
  });

  useEffect(() => {
    if (submitting || target === null) return;
    focusControl(refsRef.current[target]?.current ?? null);
    clearRef.current();
  }, [submitting, target]);
}
